/**
 * Chat Message Bubble Component
 * Renders AI / user bubbles plus embedded UI actions:
 *   – ItemSuggestionCards  (show_items)
 *   – OrderTypeSelector    (show_order_type_selector)
 *
 * Modifier picking is handled by ModifierCard rendered directly in ChatWidget,
 * not inside message bubbles.
 */

import { Avatar, Box, Typography } from '@mui/material';
import { LocalDining as LocalDiningIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import ItemSuggestionCards, { type SuggestedItem } from './ItemSuggestionCards';
import OrderTypeSelector from './OrderTypeSelector';

// ─── Types ──────────────────────────────────────────────────────────────────

export type { SuggestedItem };

export interface ChatMessageData {
  id: string;
  role: 'user' | 'model';
  content: string;
  timestamp: Date;
  // Rich UI embeds
  suggestedItems?: SuggestedItem[];
  suggestedItemsTitle?: string;
  hasMoreItems?: boolean;
  showOrderTypeSelector?: boolean;
}

// ─── Callbacks passed from ChatWidget ────────────────────────────────────────

interface ChatMessageCallbacks {
  onOrderTypeSelect?: (type: 'delivery' | 'collection') => void;
  onShowMoreItems?: () => void;
  onAddWithModifiers?: (itemName: string) => void;
  language: 'en' | 'ar';
  interactionDisabled?: boolean;
}

interface ChatMessageProps extends ChatMessageCallbacks {
  message: ChatMessageData;
}

// ─── Typing Indicator ────────────────────────────────────────────────────────

export function TypingIndicator() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, mb: 1.5 }}>
      <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main', flexShrink: 0 }}>
        <LocalDiningIcon sx={{ fontSize: 16 }} />
      </Avatar>
      <Box
        sx={{
          bgcolor: 'grey.100',
          borderRadius: '4px 16px 16px 16px',
          px: 2,
          py: 1.5,
          display: 'flex',
          gap: 0.5,
          alignItems: 'center',
        }}
      >
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: 'text.disabled',
              animation: 'chatPulse 1.2s ease-in-out infinite',
              animationDelay: `${i * 0.2}s`,
              '@keyframes chatPulse': {
                '0%, 80%, 100%': { transform: 'scale(0.6)', opacity: 0.4 },
                '40%': { transform: 'scale(1)', opacity: 1 },
              },
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

// ─── Main ChatMessage component ───────────────────────────────────────────────

export default function ChatMessage({
  message,
  language,
  onOrderTypeSelect,
  onShowMoreItems,
  onAddWithModifiers,
  interactionDisabled = false,
}: ChatMessageProps) {
  const theme = useTheme();
  const isAI = message.role === 'model';
  const isRTL = language === 'ar';
  const hasWideEmbed = !!(message.suggestedItems?.length);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isAI ? 'row' : 'row-reverse',
        alignItems: 'flex-end',
        gap: 1,
        mb: 1.5,
      }}
    >
      {/* AI Avatar */}
      {isAI && (
        <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main', flexShrink: 0 }}>
          <LocalDiningIcon sx={{ fontSize: 16 }} />
        </Avatar>
      )}

      {/* Bubble + extras */}
      <Box
        sx={{
          maxWidth: hasWideEmbed ? '95%' : '75%',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          width: hasWideEmbed ? '100%' : undefined,
        }}
      >
        {/* Main bubble */}
        {message.content && (
          <Box
            sx={{
              px: 2,
              py: 1.25,
              borderRadius: isAI ? '4px 16px 16px 16px' : '16px 4px 16px 16px',
              bgcolor: isAI
                ? theme.palette.mode === 'dark'
                  ? 'grey.800'
                  : 'grey.100'
                : 'primary.main',
              color: isAI ? 'text.primary' : 'white',
              maxWidth: '100%',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap',
                direction: isRTL ? 'rtl' : 'ltr',
                textAlign: isRTL ? 'right' : 'left',
              }}
            >
              {message.content}
            </Typography>
          </Box>
        )}

        {/* ── Item suggestion cards ── */}
        {message.suggestedItems && message.suggestedItems.length > 0 && (
          <>
            {message.suggestedItemsTitle && (
              <Typography variant="caption" sx={{ fontWeight: 600, opacity: 0.7, mb: 0.5, display: 'block' }}>
                {message.suggestedItemsTitle}
              </Typography>
            )}
            <ItemSuggestionCards
              items={message.suggestedItems}
              hasMore={message.hasMoreItems}
              language={language}
              onShowMore={onShowMoreItems}
              onAddWithModifiers={onAddWithModifiers}
            />
          </>
        )}

        {/* ── Order type selector ── */}
        {message.showOrderTypeSelector && onOrderTypeSelect && (
          <OrderTypeSelector
            language={language}
            onSelect={onOrderTypeSelect}
            disabled={interactionDisabled}
          />
        )}

        {/* Timestamp */}
        <Typography
          variant="caption"
          sx={{
            color: 'text.disabled',
            fontSize: '0.65rem',
            alignSelf: isAI ? 'flex-start' : 'flex-end',
          }}
        >
          {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </Typography>
      </Box>
    </Box>
  );
}
